import {sendAmplitudeEvent} from '#/analytics/amplitude-events';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {ActionMenuInfoTile} from '#/components/actionmenu/action-menu-info-tile';
import {ActionMenuLayoutContentNamed, ActionMenuLayoutFooter} from '#/components/actionmenu/action-menu-layout';
import {actionMenuMessageContent, useMessageCasesToContent} from '#/components/actionmenu/action-menu-message-content';
import {
  ActionMenuContentSectionOverviewBuy,
  ActionMenuContentSectionOverviewLimitBuy,
} from '#/components/actionmenu/content/action-menu-content-section-overview';
import {
  ActionMenuTradeMarketLimitTabs,
  LimitOrderTokenInputs,
  usePriceAmountSubtotal,
} from '#/components/actionmenu/sections/trade/action-menu-trade-limit';
import {TransactionVariables} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {ActionMenuFormContainer, ActionMenuTransactionReviewButton} from '#/components/actionmenu/subcomponents';
import {ActionMenuTokenInput} from '#/components/actionmenu/token-input/action-menu-token-input';
import {useActionMenuTokenInputData} from '#/components/actionmenu/token-input/use-action-menu-token-input-data';
import {MessageCase} from '#/components/actionmenu/types';
import {WalletContext, useWalletProvider} from '#/contexts/wallet-provider';
import {maxSlippageDefaultPercentage} from '#/data/max-slippage-constants';
import {useStableTokenBalance} from '#/hooks/query/use-stable-token-balance';
import {useAddFundsAction} from '#/hooks/use-add-funds-action';
import {useBlockingLoginAction} from '#/hooks/use-blocking-login-action';
import {useDismissableInfoTile} from '#/hooks/use-dismisable-info-tile';
import {TokenType} from '#/repository/types';
import {minimumTransactionFees} from '#/utils/contract-consts';
import {getTransactionFees} from '#/utils/get-transaction-fees';
import {percentageToRatio} from '#/utils/ratio-percentage';
import {decimalsMultiplier} from '@shareland/shareland-common';

import {useModalContext} from '#/contexts/modal-provider';
import environment from '#/environment/environment';
import {DefaultChildren} from '#/utils/utils';
import {TransactionType, getMinReceivedTokenAmount} from '@shareland/shareland-common';
import {FormEvent, FunctionComponent, useContext, useState} from 'react';
import {useTranslation} from 'react-i18next';

export const ActionMenuTradeBuyTab: FunctionComponent<DefaultChildren> = () => (
  <ActionMenuTradeMarketLimitTabs>
    {{
      market: <Market />,
      limit: <Limit />,
    }}
  </ActionMenuTradeMarketLimitTabs>
);

export const Market: FunctionComponent<DefaultChildren> = () => {
  const transactionType = TransactionType.Purchase;

  // Context
  const {landMarketInfo, initiateTransaction} = useContext(ActionMenuContext);
  const {usdCBalance, accountResult} = useWalletProvider();
  const {open} = useModalContext();

  // External hooks
  const [showInfoTile, onCloseTile] = useDismissableInfoTile(transactionType);
  const actionMenuTokenInputData = useActionMenuTokenInputData({
    transactionType,
    initialSelectedKey: TokenType.STABLE,
  });

  // Internal hooks
  const [maxSlippagePercentage, setMaxSlippagePercentage] = useState(maxSlippageDefaultPercentage);
  const addFundsAction = useAddFundsAction();

  // Vars
  const {stableTokenAmount, landTokenAmount} = actionMenuTokenInputData.tokenAmounts;
  const transactionFee = getTransactionFees(stableTokenAmount);
  const totalStableAmount = stableTokenAmount + transactionFee;
  const minReceivedLandAmount = getMinReceivedTokenAmount(
    landTokenAmount,
    percentageToRatio(maxSlippagePercentage, true),
  );

  const transactionVars: TransactionVariables[typeof transactionType] = {
    landTokenAmount,
    stableTokenAmount,
    marketPrice: landMarketInfo.marketPrice,
    transactionFee,
    totalStableAmount,
    maxSlippagePercentage,
    minReceivedLandAmount,
  };

  const noticeMessages = useBuyNoticeMessage(transactionVars, actionMenuTokenInputData);

  const canStartTransactionFlow = stableTokenAmount > 0 && noticeMessages.every(elem => elem?.canSubmitForm);

  const onStartTransaction = useBlockingLoginAction(async () => {
    if (!canStartTransactionFlow) return;
    sendAmplitudeEvent.buyTradeInitiated({
      event_properties: {
        amount: Number(actionMenuTokenInputData.tokenInput.input),
        wallet_address: accountResult.address,
      },
    });

    if (Number(actionMenuTokenInputData.tokenInput.input || 0) > usdCBalance.value) {
      addFundsAction();
      return;
    }

    if (environment.isWaitlistNet) {
      open({
        name: 'CountryNotSupportedModal',
        props: {},
      });
      return;
    }

    initiateTransaction({
      type: transactionType,
      data: transactionVars,
    });
  });

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    onStartTransaction();
  };

  return (
    <ActionMenuFormContainer onSubmit={onSubmit}>
      <ActionMenuLayoutContentNamed noticeMessages={noticeMessages}>
        {{
          info: showInfoTile && <ActionMenuInfoTile onClose={onCloseTile} tileKey={transactionType} />,
          grow: <ActionMenuTokenInput tokenInputData={actionMenuTokenInputData} />,
          overview: (
            <ActionMenuContentSectionOverviewBuy
              transactionVars={transactionVars}
              setMaxSlippagePercentage={setMaxSlippagePercentage}
            />
          ),
        }}
      </ActionMenuLayoutContentNamed>

      <ActionMenuLayoutFooter>
        <ActionMenuTransactionReviewButton disabled={!canStartTransactionFlow} />
      </ActionMenuLayoutFooter>
    </ActionMenuFormContainer>
  );
};

// commentng this code in case we need this in the future
const useBuyNoticeMessage = (
  transactionVars: TransactionVariables[TransactionType.Purchase],
  actionMenuTokenInputData: ReturnType<typeof useActionMenuTokenInputData>,
) => {
  const {
    accountResult: {isConnected},
  } = useContext(WalletContext);
  const {t} = useTranslation('translation');

  const {data: stableTokenBalance} = useStableTokenBalance();
  const addFundsAction = useAddFundsAction(
    transactionVars?.stableTokenAmount ? transactionVars?.stableTokenAmount / decimalsMultiplier : undefined,
  );

  const cases: Record<string, MessageCase> = {
    ...(isConnected && {
      notEnoughStableTokenInWalletToPurchase: {
        condition: () => transactionVars.totalStableAmount > Number(stableTokenBalance),
        message: () => actionMenuMessageContent.notEnoughUsdc(t, addFundsAction),
      },
    }),
    maxSlippageTooLowTransactionLikelyToFail: {
      condition: () => transactionVars.maxSlippagePercentage < maxSlippageDefaultPercentage,
      message: () =>
        actionMenuMessageContent.transactionLikelyToFailMaxSlippageValueLower(t, maxSlippageDefaultPercentage),
    },
  };
  return useMessageCasesToContent(cases, {
    usdcAmount: transactionVars.stableTokenAmount,
    inputValidationMessage: actionMenuTokenInputData.tokenInput.validationMessage,
  });
};

export const Limit: FunctionComponent<DefaultChildren> = () => {
  // Context
  const {landMarketInfo, initiateTransaction} = useContext(ActionMenuContext);

  // External hooks
  const [showInfoTile, onCloseTile] = useDismissableInfoTile(TransactionType.Purchase);

  // Internal hooks
  const {price, amount, subtotal} = usePriceAmountSubtotal(landMarketInfo.marketPrice);

  // Vars
  const purchaseTransactionFee = getTransactionFees(subtotal.value);

  const transactionVars: TransactionVariables[TransactionType.PurchaseLimitOrder] = {
    landTokenAmount: Math.round(amount.value),
    stableTokenAmount: Math.round(subtotal.value),
    purchaseTransactionFee,
    transactionFee: minimumTransactionFees,
    totalStableAmount: Math.round(subtotal.value + purchaseTransactionFee + minimumTransactionFees),
    averagePrice: Math.round(price.value),
  };

  const noticeMessages = useLimitTokenNoticeMessage(transactionVars);

  const canStartTransactionFlow =
    !!amount.value && !!subtotal.value && noticeMessages.every(elem => elem.canSubmitForm);

  const onStartTransaction = useBlockingLoginAction(async () => {
    if (!canStartTransactionFlow) return;

    initiateTransaction({
      type: TransactionType.PurchaseLimitOrder,
      data: transactionVars,
    });
  });

  // Functions
  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    onStartTransaction();
  };

  return (
    <ActionMenuFormContainer onSubmit={onSubmit}>
      <ActionMenuLayoutContentNamed noticeMessages={noticeMessages}>
        {{
          info: showInfoTile && <ActionMenuInfoTile onClose={onCloseTile} tileKey={TransactionType.Purchase} />,
          grow: (
            <div className='space-y-4'>
              <LimitOrderTokenInputs
                type={TransactionType.PurchaseLimitOrder}
                price={price}
                amount={amount}
                subtotal={subtotal}
              />
            </div>
          ),
          overview: <ActionMenuContentSectionOverviewLimitBuy transactionVars={transactionVars} />,
        }}
      </ActionMenuLayoutContentNamed>

      <ActionMenuLayoutFooter>
        <ActionMenuTransactionReviewButton disabled={!canStartTransactionFlow} />
      </ActionMenuLayoutFooter>
    </ActionMenuFormContainer>
  );
};

const useLimitTokenNoticeMessage = (transactionVars: TransactionVariables[TransactionType.PurchaseLimitOrder]) => {
  const {landMarketInfo} = useContext(ActionMenuContext);
  const {
    accountResult: {isConnected},
  } = useContext(WalletContext);
  const {t} = useTranslation('translation');

  const {data: stableTokenBalance} = useStableTokenBalance();

  const cases: Record<string, MessageCase> = {
    ...(isConnected && {
      notEnoughStableTokenInWalletToPurchase: {
        condition: () => transactionVars.totalStableAmount > Number(stableTokenBalance),
        message: () => actionMenuMessageContent.notEnoughUsdc(t),
      },
    }),
    mightProcessAsMarketOrder: {
      condition: () =>
        !!transactionVars.landTokenAmount &&
        !!transactionVars.stableTokenAmount &&
        transactionVars.averagePrice >= landMarketInfo.marketPrice,
      message: () => actionMenuMessageContent.limitBuyMightProcessAsMarketOrder(t),
    },
  };

  return useMessageCasesToContent(cases, {
    usdcAmount: transactionVars.stableTokenAmount,
  });
};
